/* eslint-disable react/no-unescaped-entities */
/**
 * CompromisWidget.jsx
 * ====================
 * Widget Cockpit Dirigeant — Pipeline de conversion compromis → acte.
 * Affiche : taux chute, délai acte, motifs de chute, financement.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import {
    FileSignature, CheckCircle2, XCircle, AlertTriangle, Loader2, TrendingDown,
} from 'lucide-react';

const REASON_LABELS = {
    financing_denied: 'Refus financement',
    buyer_retract: 'Rétract. acheteur',
    seller_retract: 'Rétract. vendeur',
    inspection: 'Diagnostic',
    conditions: 'Conditions',
    other: 'Autre',
};

export default function CompromisWidget() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const { data: res } = await api.get('/kpi/compute', {
                    params: { ids: 'accept_to_compromis,compromis_fallthrough,compromis_fallthrough_reason,financing_fallthrough,compromis_to_acte,total_fallthrough' }
                });
                setData(res?.results || {});
            } catch (e) {
                // silent
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="rounded-2xl p-4 bg-card border border-white/10 space-y-2" data-testid="compromis-widget-loading">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white/5 animate-pulse" />
                    <div className="h-3 w-48 rounded bg-white/5 animate-pulse" />
                </div>
                <div className="grid grid-cols-4 gap-2">
                    {[0,1,2,3].map(i => <div key={i} className="h-12 rounded-lg bg-white/[0.03] animate-pulse" />)}
                </div>
            </div>
        );
    }

    const fallPct = data.compromis_fallthrough?.value;
    const totalCompromis = data.compromis_fallthrough?.meta?.total || 0;
    const fellCount = data.compromis_fallthrough?.meta?.fell_through || 0;
    const acteDays = data.compromis_to_acte?.value;
    const accDays = data.accept_to_compromis?.value;
    const finDeniedPct = data.financing_fallthrough?.value;
    const reasons = data.compromis_fallthrough_reason?.meta?.reasons || {};
    const fallClass = fallPct === null || fallPct === undefined ? 'text-muted-foreground' : fallPct <= 5 ? 'text-green-400' : fallPct <= 15 ? 'text-amber-400' : 'text-red-400';

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-2xl overflow-hidden border border-green-500/20 bg-gradient-to-br from-green-500/10 via-card to-emerald-500/5"
            data-testid="compromis-widget"
        >
            <div className="relative p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shrink-0">
                        <FileSignature className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-green-400 font-bold uppercase tracking-wider">Pipeline compromis → acte</p>
                        <p className="text-base font-bold text-foreground">{totalCompromis} compromis suivi{totalCompromis > 1 ? 's' : ''}</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                    <Tile
                        label="Taux chute"
                        value={fallPct !== null && fallPct !== undefined ? `${fallPct}%` : '—'}
                        sub={`${fellCount} / ${totalCompromis}`}
                        color={fallClass}
                        icon={XCircle}
                    />
                    <Tile
                        label="Délai acte"
                        value={acteDays !== null && acteDays !== undefined ? `${acteDays} j` : '—'}
                        sub="signature → acte"
                        icon={CheckCircle2}
                    />
                    <Tile
                        label="Acceptation → signature"
                        value={accDays !== null && accDays !== undefined ? `${accDays} j` : '—'}
                        sub="offre → compromis"
                        icon={FileSignature}
                    />
                    <Tile
                        label="Refus financement"
                        value={finDeniedPct !== null && finDeniedPct !== undefined ? `${finDeniedPct}%` : '—'}
                        sub="sur total"
                        color={finDeniedPct > 10 ? 'text-red-400' : 'text-foreground'}
                        icon={AlertTriangle}
                    />
                </div>

                {/* Répartition motifs chute */}
                {Object.keys(reasons).length > 0 && (
                    <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/20">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-red-300 mb-2 flex items-center gap-1.5">
                            <TrendingDown className="w-3 h-3" />
                            Motifs de chute
                        </p>
                        <div className="space-y-1.5">
                            {Object.entries(reasons).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                                <div key={reason} className="flex items-center gap-2 text-[11px]">
                                    <span className="flex-1 text-muted-foreground">{REASON_LABELS[reason] || reason}</span>
                                    <span className="text-foreground font-bold">{count}</span>
                                    <div className="w-12 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                        <div
                                            className="h-full bg-red-400"
                                            style={{ width: `${Math.min(100, (count / fellCount) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {totalCompromis === 0 && (
                    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 text-center">
                        <p className="text-xs text-muted-foreground">
                            Aucun compromis enregistré pour l'instant. Depuis une fiche mandat, ouvrez la section "Compromis / Acte authentique" pour suivre le closing.
                        </p>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

function Tile({ label, value, sub, color = 'text-foreground', icon: Icon }) {
    return (
        <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/10">
            <div className="flex items-center gap-1.5 mb-1">
                {Icon && <Icon className={`w-3 h-3 ${color}`} />}
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold truncate">{label}</p>
            </div>
            <p className={`text-lg font-black leading-none ${color}`}>{value}</p>
            {sub && <p className="text-[9px] text-muted-foreground mt-1 truncate">{sub}</p>}
        </div>
    );
}
